import { useLingui } from '@lingui/react/macro'
import { ColumnDef } from '@tanstack/react-table'
import { Dropdown } from 'components/form/inputs/Dropdown'
import { TextInput } from 'components/form/inputs/TextInput'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { DropdownOption, FormField } from 'types'

const ConfigurationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const FieldSelectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const ValueInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

export interface BulkActionModalEditConfigurationProps<TData> {
  editableFields: {
    label: string
    value: string | FormField | ColumnDef<TData>
  }[]
  selectedFieldKey: string
  setSelectedFieldKey: React.Dispatch<React.SetStateAction<string>>
  newFieldValue: string | number | string[] | undefined
  setNewFieldValue: React.Dispatch<
    React.SetStateAction<string | number | string[] | undefined>
  >
}

export const BulkActionModalEditConfiguration = <TData,>({
  editableFields,
  selectedFieldKey,
  setSelectedFieldKey,
  setNewFieldValue,
  newFieldValue,
}: BulkActionModalEditConfigurationProps<TData>) => {
  const { t } = useLingui()
  const fieldDropdownOptions: DropdownOption[] = useMemo(() => {
    return editableFields.map((field) => {
      let displayLabel = field.label

      if (typeof field.value === 'object') {
        if ('inputType' in field.value) {
          // FormField - use .label
          displayLabel = (field.value as FormField).label || field.label
        } else if ('header' in field.value) {
          // ColumnDef use .header
          const columnDefHeader = (field.value as ColumnDef<TData>).header
          if (typeof columnDefHeader === 'string') {
            // fallback to field.label
            displayLabel = columnDefHeader || field.label
          }
        }
      }

      return {
        value: field.label, // Using dataKey as the key for selection (field.label now contains dataKey)
        label: displayLabel,
      }
    })
  }, [editableFields])

  // Find the selected field configuration
  const selectedFieldConfiguration = useMemo(() => {
    return editableFields.find((field) => field.label === selectedFieldKey)
  }, [editableFields, selectedFieldKey])

  // Determine the field type and render appropriate input
  const renderValueInput = () => {
    if (!selectedFieldConfiguration) {
      return null
    }

    const { value: fieldValue } = selectedFieldConfiguration

    // Case 1: Simple string field (label: string, value: string)
    if (typeof fieldValue === 'string') {
      return (
        <TextInput
          $label={t`New Value`}
          $labelLocation='top'
          placeholder={t`Enter the new value`}
          value={newFieldValue?.toString()}
          onChange={(e) => setNewFieldValue(e.target.value)}
        />
      )
    }

    // Case 2: FormField type (label: string, value: FormField)
    if (fieldValue && 'inputType' in fieldValue) {
      const formField = fieldValue as FormField

      // For dropdown fields, render a simple dropdown
      if (formField.inputType === 'dropdown' && formField.options) {
        const dropdownOptions: DropdownOption[] = formField.options.map(
          (option) => ({
            value: option.value,
            label: option.label,
          })
        )

        return (
          <Dropdown
            $label={t`New Value`}
            $labelLocation='top'
            options={dropdownOptions}
            onValueChange={(value) => setNewFieldValue(value)}
            defaultValue={newFieldValue?.toString()}
            placeholder={t`Select a value`}
            cleanable={false}
          />
        )
      }

      // For other FormField types, render a simple text input
      return (
        <TextInput
          $label={t`New Value`}
          $labelLocation='top'
          placeholder={t`Enter the new value`}
          value={newFieldValue?.toString()}
          onChange={(e) => setNewFieldValue(e.target.value)}
        />
      )
    }

    // Case 3: ColumnDef type (label: string, value: ColumnDef)
    else if (
      fieldValue &&
      typeof fieldValue === 'object' &&
      'accessorKey' in fieldValue
    ) {
      return (
        <TextInput
          $label={t`New Value`}
          $labelLocation='top'
          placeholder={t`Enter the new value`}
          value={newFieldValue?.toString()}
          onChange={(e) => setNewFieldValue(e.target.value)}
        />
      )
    }
  }

  return (
    <ConfigurationContainer>
      <FieldSelectionContainer>
        <Dropdown
          $label={t`Field to Edit`}
          $labelLocation='top'
          options={fieldDropdownOptions}
          onValueChange={(e) => {
            setSelectedFieldKey(e)
          }}
          defaultValue={selectedFieldKey}
          placeholder={t`Select a field to edit`}
          cleanable={false}
        />
      </FieldSelectionContainer>

      {selectedFieldKey && (
        <ValueInputContainer>{renderValueInput()}</ValueInputContainer>
      )}
    </ConfigurationContainer>
  )
}
